/**
 * ITER295 — Manager Tickets
 *
 * Manager-shell view of Operational Tickets. Auto-scoped to the caller's
 * outlet on the backend via role gating; here we simply omit the outlet
 * filter and lean on the existing `/api/tickets` endpoint's tenant scope.
 *
 * Delegates every heavy component (list, detail modal, manual create) to
 * `AdminTickets.js` — a manager gets the same working surface without the
 * escalation / failure-register admin tabs.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import {
  Search, RefreshCw, Ticket as TicketIcon, Plus, AlertTriangle, Clock,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import BackToDashboard from '../components/ops/BackToDashboard';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  open:        { bg: '#FEF3C7', fg: '#B45309', label: 'Open' },
  in_progress: { bg: '#DBEAFE', fg: '#1D4ED8', label: 'In Progress' },
  resolved:    { bg: '#D1FAE5', fg: '#065F46', label: 'Resolved' },
  closed:      { bg: '#E5E7EB', fg: '#374151', label: 'Closed' },
};
const PRIORITY_STYLE = {
  low:      { bg: '#F3F4F6', fg: '#4B5563', label: 'Low' },
  medium:   { bg: '#E0E7FF', fg: '#3730A3', label: 'Medium' },
  high:     { bg: '#FED7AA', fg: '#9A3412', label: 'High' },
  critical: { bg: '#FECACA', fg: '#991B1B', label: 'Critical' },
};

function fmtAge(hrs) {
  if (hrs == null) return '—';
  if (hrs < 1)   return `${Math.round(hrs * 60)}m`;
  if (hrs < 24)  return `${hrs.toFixed(1)}h`;
  return `${Math.round(hrs / 24)}d`;
}

// Reuse the Admin surfaces for the detail + create modals via the local
// Mgr* components declared below (no React.lazy needed).

export default function ManagerTickets() {
  const { getHeaders } = useAuth();
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  // ITER300 · seed local state from URL query params so KPI drill-throughs
  // land pre-filtered.
  const [q, setQ] = useState(() => searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(
    () => searchParams.get('status') || 'open,in_progress',
  );
  const [priorityFilter, setPriorityFilter] = useState(
    () => searchParams.get('priority') || '',
  );
  const [overdueOnly, setOverdueOnly] = useState(
    () => searchParams.get('overdue') === 'true',
  );
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [departments, setDepartments] = useState(['Maintenance']);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (statusFilter)   p.set('status', statusFilter);
      if (priorityFilter) p.set('priority', priorityFilter);
      if (overdueOnly)    p.set('overdue', 'true');
      const res = await axios.get(`${API}/api/tickets?${p}`, { headers: getHeaders() });
      setTickets(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load tickets');
    } finally { setLoading(false); }
  }, [statusFilter, priorityFilter, overdueOnly, getHeaders]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/departments`,
                                    { headers: getHeaders() });
        setDepartments(res.data?.departments || ['Maintenance']);
      } catch { /* non-fatal */ }
    })();
  }, [getHeaders]);

  const filtered = useMemo(() => {
    if (!q.trim()) return tickets;
    const ql = q.toLowerCase();
    return tickets.filter((t) =>
      (t.title || '').toLowerCase().includes(ql) ||
      (t.ticket_id || '').toLowerCase().includes(ql),
    );
  }, [tickets, q]);

  const counts = useMemo(() => {
    const c = { open: 0, in_progress: 0, overdue: 0 };
    for (const t of tickets) {
      if (t.status === 'open')        c.open++;
      if (t.status === 'in_progress') c.in_progress++;
      if (t.overdue)                  c.overdue++;
    }
    return c;
  }, [tickets]);

  const patch = async (id, payload) => {
    try {
      const res = await axios.patch(`${API}/api/tickets/${id}`, payload,
                                    { headers: getHeaders() });
      setTickets((prev) => prev.map((x) => x.id === res.data.id ? res.data : x));
      if (detail?.id === id) setDetail(res.data);
      toast.success('Ticket updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="manager-tickets-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <BackToDashboard />
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500 mb-1">
              Operations
            </div>
            <h1 className="text-2xl font-semibold text-slate-900">Tickets</h1>
            <p className="text-xs text-slate-500 mt-1">
              Every open operational issue for your outlet.
            </p>
          </div>
          <Button onClick={() => setCreating(true)} data-testid="mgr-new-ticket-btn">
            <Plus className="w-4 h-4 mr-1" /> Raise Ticket
          </Button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          {[
            { k: 'Open', v: counts.open, icon: TicketIcon, color: '#334155' },
            { k: 'In Progress', v: counts.in_progress, icon: Clock, color: '#1D4ED8' },
            { k: 'Overdue', v: counts.overdue, icon: AlertTriangle, color: '#B91C1C' },
          ].map((k) => (
            <div key={k.k} className="bg-white rounded-lg border px-4 py-3">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">
                <k.icon className="w-3 h-3" style={{ color: k.color }} /> {k.k}
              </div>
              <div className="text-2xl font-semibold mt-1" style={{ color: k.color }}>
                {k.v}
              </div>
            </div>
          ))}
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap gap-2 items-center mb-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input className="pl-8 w-64" value={q}
                   onChange={(e) => setQ(e.target.value)}
                   placeholder="Search title / ticket id"
                   data-testid="mgr-ticket-search" />
          </div>
          <Select value={statusFilter || 'any'}
                  onValueChange={(v) => setStatusFilter(v === 'any' ? '' : v)}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any status</SelectItem>
              <SelectItem value="open,in_progress">Active</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={priorityFilter || 'any'}
                  onValueChange={(v) => setPriorityFilter(v === 'any' ? '' : v)}>
            <SelectTrigger className="w-36" data-testid="mgr-ticket-priority">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any priority</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high,critical">High+Critical</SelectItem>
              <SelectItem value="medium,high,critical">Medium+</SelectItem>
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={() => setOverdueOnly(v => !v)}
            data-testid="mgr-ticket-overdue-filter"
            className={`inline-flex items-center gap-1 h-9 px-3 rounded-md border text-[12px] font-medium transition-colors ${
              overdueOnly
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            Overdue only
          </button>
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>

        {/* Ticket list — card-per-row for the manager shell */}
        <div className="space-y-2" data-testid="mgr-ticket-list">
          {loading ? (
            <div className="text-center text-slate-500 py-8">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-slate-500 py-10 bg-white rounded-lg border">
              <TicketIcon className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              Nothing outstanding right now.
            </div>
          ) : filtered.map((t) => {
            const s = STATUS_STYLE[t.status] || STATUS_STYLE.open;
            const p = PRIORITY_STYLE[t.priority] || PRIORITY_STYLE.medium;
            return (
              <div key={t.id}
                   onClick={() => setDetail(t)}
                   data-testid={`mgr-ticket-${t.ticket_id}`}
                   className="cursor-pointer bg-white rounded-lg border px-4 py-3 hover:border-slate-400">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] font-mono text-slate-500 mb-0.5">
                      {t.ticket_id} · {t.department}
                      {t.source_type === 'room_checklist_failure' && (
                        <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-semibold
                                         bg-indigo-50 text-indigo-800 border border-indigo-200"
                              data-testid={`mgr-room-badge-${t.ticket_id}`}>
                          ROOM {t.room_number || '—'}
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {t.title}
                    </div>
                    {t.description && (
                      <div className="text-xs text-slate-500 line-clamp-2 mt-0.5">
                        {t.description}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <div className="flex gap-1">
                      <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full"
                            style={{ background: p.bg, color: p.fg }}>{p.label}</span>
                      <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full"
                            style={{ background: s.bg, color: s.fg }}>{s.label}</span>
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {fmtAge(t.age_hours)} old
                      {t.overdue && <span className="text-red-700 font-semibold ml-1">· OVERDUE</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Inline detail dialog */}
      {detail && (
        <MgrTicketDetail
          ticket={detail}
          departments={departments}
          onClose={() => setDetail(null)}
          onPatch={(payload) => patch(detail.id, payload)}
        />
      )}
      {creating && (
        <MgrManualTicket
          departments={departments}
          onClose={() => setCreating(false)}
          onCreated={() => load()}
        />
      )}
    </div>
  );
}

// ==================================================================
// Manager-side detail modal — same transitions, no dept reassignment
// ==================================================================
const MANAGER_STATUS_ROLES = new Set(['supervisor', 'manager', 'area_manager']);

function MgrTicketDetail({ ticket, onClose, onPatch }) {
  const { user, getHeaders } = useAuth();
  const [notes, setNotes] = useState(ticket.resolution_notes || '');
  const [photos, setPhotos] = useState(Array.isArray(ticket.resolution_photos) ? [...ticket.resolution_photos] : []);
  const [uploading, setUploading] = useState(false);
  const s = STATUS_STYLE[ticket.status] || STATUS_STYLE.open;
  const p = PRIORITY_STYLE[ticket.priority] || PRIORITY_STYLE.medium;
  const canChangeStatus = MANAGER_STATUS_ROLES.has((user?.role || '').toLowerCase());
  const canClose = canChangeStatus ||
    ((ticket.created_by || '').toLowerCase() === (user?.email || '').toLowerCase());
  const remaining = ticket.time_remaining_hours;

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/upload/logo`, fd, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.file_url) {
        setPhotos((prev) => [...prev, res.data.file_url]);
        toast.success('Photo attached');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Photo upload failed');
    } finally { setUploading(false); }
  };

  const resolveWithPhotos = () => onPatch({
    status: 'resolved',
    resolution_notes: notes.trim() || null,
    resolution_photos: photos,
  });
  const closeTicket = () => onPatch({
    status: 'closed',
    resolution_notes: notes.trim() || null,
    resolution_photos: photos,
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="mgr-ticket-detail-modal">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-mono text-xs text-[#5C6B64]">{ticket.ticket_id}</div>
              <DialogTitle className="text-lg text-[#1A2421]">{ticket.title}</DialogTitle>
            </div>
            <div className="flex gap-1">
              <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full"
                    style={{ background: p.bg, color: p.fg }}>{p.label}</span>
              <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full"
                    style={{ background: s.bg, color: s.fg }}>{s.label}</span>
            </div>
          </div>
        </DialogHeader>
        <div className="space-y-3">
          {ticket.description && (
            <div className="text-sm text-[#1A2421] whitespace-pre-wrap">
              {ticket.description}
            </div>
          )}
          <div className="text-xs text-[#5C6B64]">
            {ticket.department} · {fmtAge(ticket.age_hours)} old · SLA {ticket.sla_hours}h ·{' '}
            <span className={remaining < 0 ? 'text-red-700 font-semibold' : 'text-[color:var(--color-primary)]'}
                  data-testid="mgr-sla-remaining">
              {remaining == null ? '—'
                : remaining < 0
                  ? `${Math.abs(remaining).toFixed(1)}h overdue`
                  : `${remaining.toFixed(1)}h left`}
            </span>
          </div>

          {(ticket.activity_log || []).length > 0 && (
            <div className="rounded border border-[#E2E8DF] bg-[#F9F9F8] p-2 max-h-32 overflow-y-auto"
                 data-testid="mgr-activity-log">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#5C6B64] mb-1">
                Activity ({ticket.activity_log.length})
              </div>
              {ticket.activity_log.slice().reverse().map((a) => (
                <div key={a.id} className="text-xs text-[#1A2421] py-1 border-b border-[#E2E8DF] last:border-b-0">
                  <span className="text-[10px] text-[#5C6B64] mr-1">
                    {a.by_name || a.by_email} · {new Date(a.at).toLocaleString()}
                  </span>
                  {a.message}
                </div>
              ))}
            </div>
          )}

          {!canChangeStatus && (
            <div className="rounded border border-[#E2E8DF] bg-[#F9F9F8] p-2 text-xs text-[#5C6B64]"
                 data-testid="mgr-view-only-notice">
              You have view-only access. Ask your supervisor or manager to update status.
            </div>
          )}

          {canChangeStatus && (
            <>
              <div>
                <Label className="text-[#1A2421]">Resolution Notes</Label>
                <Textarea rows={3} value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="What was done? Who verified?"
                          data-testid="mgr-resolution-notes" />
              </div>
              <div>
                <Label className="text-[#1A2421]">Resolution Photos ({photos.length})</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {photos.map((url, i) => (
                    <div key={i} className="relative w-16 h-16 rounded border border-[#E2E8DF] overflow-hidden">
                      <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}
                        className="absolute top-0 right-0 bg-black/60 text-white text-[10px] px-1 rounded-bl"
                        data-testid={`mgr-photo-remove-${i}`}
                      >×</button>
                    </div>
                  ))}
                  <label className="w-16 h-16 flex items-center justify-center rounded border border-dashed
                                    border-[#E2E8DF] cursor-pointer hover:bg-[#F9F9F8] text-xs text-[#5C6B64]"
                         data-testid="mgr-photo-upload-btn">
                    {uploading ? '…' : '+ Add'}
                    <input type="file" accept="image/*" className="hidden"
                           onChange={(e) => uploadPhoto(e.target.files?.[0])}
                           data-testid="mgr-photo-upload-input" />
                  </label>
                </div>
              </div>
            </>
          )}
        </div>
        <DialogFooter className="flex flex-wrap gap-2">
          {canChangeStatus && ticket.status === 'open' && (
            <Button variant="outline" onClick={() => onPatch({ status: 'in_progress' })}
                    data-testid="mgr-start-work">
              Start Work
            </Button>
          )}
          {canChangeStatus && (ticket.status === 'open' || ticket.status === 'in_progress') && (
            <Button onClick={resolveWithPhotos}
                    className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                    data-testid="mgr-resolve">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Mark Resolved
            </Button>
          )}
          {ticket.status === 'resolved' && canClose && (
            <Button onClick={closeTicket}
                    className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                    data-testid="mgr-close">
              Close Ticket
            </Button>
          )}
          {canChangeStatus && ticket.status !== 'open' && ticket.status !== 'in_progress' && (
            <Button variant="outline" onClick={() => onPatch({ status: 'open' })}
                    data-testid="mgr-reopen">
              Re-open
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MgrManualTicket({ onClose, onCreated, departments }) {
  const { getHeaders, user } = useAuth();
  const [form, setForm] = useState({
    outlet_id: user?.outlet_id || '',
    department: departments[0] || 'Maintenance',
    priority: 'medium',
    sla_hours: 24,
    title: '',
    description: '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.outlet_id) {
      toast.error('Your account is not linked to an outlet — contact admin.');
      return;
    }
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/tickets`, {
        ...form, sla_hours: Number(form.sla_hours) || 24,
      }, { headers: getHeaders() });
      toast.success(`Ticket ${res.data.ticket_id} raised`);
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="mgr-manual-ticket-modal">
        <DialogHeader><DialogTitle>Raise Operational Ticket</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Department</Label>
            <Select value={form.department}
                    onValueChange={(v) => setForm({ ...form, department: v })}>
              <SelectTrigger data-testid="mgr-mt-dept"><SelectValue /></SelectTrigger>
              <SelectContent>
                {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Title</Label>
            <Input value={form.title}
                   onChange={(e) => setForm({ ...form, title: e.target.value })}
                   placeholder="Brief summary of the issue"
                   data-testid="mgr-mt-title" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={3} value={form.description}
                       onChange={(e) => setForm({ ...form, description: e.target.value })}
                       placeholder="Location, severity, what has been tried already…" />
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={form.priority}
                    onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="mgr-mt-save">
            {saving ? 'Creating…' : 'Raise Ticket'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
